import { io, Socket } from 'socket.io-client'
import { getAccessToken } from './axios'

let socket: Socket | null = null

/**
 * Instance Socket.io singleton — partagée dans toute l'application.
 * Connexion lazy : créée seulement au premier appel à getSocket().
 *
 * FIX : namespace '/chat' ajouté pour correspondre au backend ChatGateway.
 * Le backend déclare @WebSocketGateway({ namespace: '/chat' }) donc le
 * frontend doit se connecter à 'http://localhost:4000/chat' et non à la racine.
 *
 * Le proxy Vite '/socket.io' proxifie automatiquement '/chat/socket.io' aussi
 * car socket.io-client ajoute le path /socket.io/ en interne.
 */
export function getSocket(): Socket {
  if (!socket) {
    const token = getAccessToken()
    const baseUrl = import.meta.env.VITE_SOCKET_URL ?? window.location.origin

    socket = io(`${baseUrl}/chat`, {   // ← FIX : namespace /chat
      auth: { token },
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      transports: ['websocket', 'polling'],
      path: '/socket.io',              // path explicite pour le proxy Vite
    })

    socket.on('connect', () => {
      console.log('[socket] Connecté —', socket?.id)
    })

    socket.on('disconnect', (reason) => {
      console.log('[socket] Déconnecté —', reason)
    })

    socket.on('connect_error', (err) => {
      console.warn('[socket] Erreur connexion —', err.message)
    })

    // Erreur d'auth renvoyée par le gateway
    socket.on('error', (data: any) => {
      console.error('[socket] Erreur gateway —', data?.message ?? data)
    })
  }

  return socket
}

/**
 * Connecte le socket (appeler après login).
 * Met à jour le token AVANT la connexion pour que le handshake JWT soit valide.
 */
export function connectSocket(): void {
  const s = getSocket()
  // Toujours rafraîchir le token avant de se (re)connecter
  const token = getAccessToken()
  if (token) s.auth = { token }

  if (!s.connected) {
    s.connect()
  }
}

/**
 * Reconnecter le socket avec un token fraîchement rafraîchi.
 * Appelé automatiquement par l'intercepteur axios après chaque refresh réussi.
 * Évite que le socket WebSocket reste avec un token expiré après renouvellement HTTP.
 */
export function reconnectSocketWithToken(newToken: string): void {
  if (!socket) return

  // Mettre à jour le token d'auth pour la prochaine connexion
  socket.auth = { token: newToken }

  if (socket.connected) {
    // Forcer une reconnexion propre avec le nouveau token
    socket.disconnect().connect()
  } else {
    socket.connect()
  }
}

/** Déconnecte proprement (appeler lors du logout) */
export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect()
    socket = null  // reset le singleton pour recréer avec un nouveau token au prochain login
  }
}

/** Rejoint une room Socket.io */
export function joinRoom(room: string): void {
  getSocket().emit('join', room)
}

/** Quitte une room Socket.io */
export function leaveRoom(room: string): void {
  getSocket().emit('leave', room)
}

export type { Socket }
